import * as React from 'react';
import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type Key,
} from 'react';
import type {Node} from '@react-types/shared';
import {assignProps, view} from '@sail/engine';
import {search, cancelCircleFilled} from '@sail/icons/react/Icon';
import type {View} from '@sail/engine';
import type {ListCollection, TreeState} from '@sail/react-aria';
import {
  FocusScope,
  useComboBox,
  useListCollection,
  useMultiComboBoxState,
} from '@sail/react-aria';
import {useFormFieldProps} from './FormField';
import {useSearchFieldFormAdapter} from './form/adapters/useSearchFieldFormAdapter';
import {useFormContext} from './form/useFormContext';
import {Icon} from './Icon';
import {CompositeField, CompositeFieldConfig} from './CompositeField';
import {Popover} from './popover';
import type {DismissValue} from './popover';
import {Action} from './Action';
import {TextField, TextFieldConfig} from './TextField';
import type {ListBoxProps} from './ListBox';
import type {FauxTreeProps} from './FauxTree';
import type {FauxTreeGroupProps} from './FauxTreeGroup';
import type {FauxTreeItemProps} from './FauxTreeItem';
import {ListBox, ListBoxContext} from './ListBox';
import {DO_NOT_USE_FauxTree as FauxTree, FauxTreeContext} from './FauxTree';
import {FauxTreeGroupTransforms} from './FauxTreeGroup';
import {FauxTreeItemTransforms} from './FauxTreeItem';
import {Tooltip} from './Tooltip';

import {css} from '../css';
import {type SearchFieldProps, SearchFieldConfig} from './SearchFieldConfig';
import {getFullDataItems} from '../hooks/useFauxTreeState';
import {useMultiSelectTriggerContent as getVisibleItemsAndOverflow} from '../hooks/useMultiSelectTriggerContent';
import {useMediaQuery} from '../providers/UiMediaQueryProvider';
import {
  normalizeSelectionProps,
  validateSelectionProps,
} from './utils/selectionHelpers';

type MessagesConfig = {
  selectedText?: (t: number) => string;
  numberOfItemsText?: (t: number) => string;
  andMoreText?: (t: number) => string;
};

const DISMISS_ON_ESCAPE: DismissValue = {
  outsidePress: false,
  outsideFocus: false,
  escapeKey: true,
};

// NOTE: Using TextField size properties to set text styles of the temporary
// labels used to calculate the visible items and avoid using
// window.getComputedStyle to get styles at runtime, which is expensive in terms of performance
// TextField size variants css properties: https://git.corp.stripe.com/stripe-internal/pay-server/blob/81bc4561d4b69bfa635778604d813ba079917ce8/frontend/sail-next/packages/ui/src/components/TextField.tsx#L94-L109
// Font tokens: https://git.corp.stripe.com/stripe-internal/pay-server/blob/81bc4561d4b69bfa635778604d813ba079917ce8/frontend/sail-next/packages/ui/src/tokens/font/index.ts#L349-L371
const textStylePropertiesBySize = {
  // font: 'label.small' equivalent
  small: {
    fontSize: '12px',
    lineHeight: '16px',
  },
  // font: 'label.medium' equivalent
  medium: {
    fontSize: '14px',
    lineHeight: '20px',
  },
  // font: 'label.large' equivalent
  large: {
    fontSize: '16px',
    lineHeight: '24px',
  },
};

const defaultAndMoreItemsText = (itemsCount: number) =>
  `and ${itemsCount} more`;

const defaultNumberOfItemsText = (itemsCount: number) => `${itemsCount} items`;

const defaultSelectedTextForSearch = (
  itemsCount: number,
  totalCount: number,
) => {
  if (itemsCount > 0) {
    const allSelected = itemsCount === totalCount;
    if (allSelected) {
      return `Deselect all`;
    }
    return `${itemsCount} of ${totalCount} selected`;
  } else {
    return `Select all`;
  }
};

const getCollectionItemsLength = (
  collection: ListCollection<FauxTreeItemProps, FauxTreeGroupProps>,
) => {
  return collection.getItems().reduce((acc, item) => {
    if (item.childItems) {
      return acc + item.childItems.length;
    } else {
      return acc + 1;
    }
  }, 0);
};

const getComputedInputValue = (
  selectedItems: (Node<unknown> | null)[],
  visibleItemsCount: number,
  firstItemOverflows: boolean,
  firstItemOverflowText: string,
  messages?: MessagesConfig,
): string => {
  // All items are visible
  let value = selectedItems
    .slice(0, visibleItemsCount)
    .map((item) => item?.textValue)
    .join(', ');

  // No item labels are visible
  if (visibleItemsCount === 0) {
    value = messages?.numberOfItemsText
      ? messages.numberOfItemsText(selectedItems.length)
      : defaultNumberOfItemsText(selectedItems.length);
  }
  // The only item's label is partially visible
  else if (selectedItems.length === 1 && firstItemOverflows) {
    value = firstItemOverflowText;
  }
  // Some item labels and a the hidden items counter is visible
  else if (visibleItemsCount < selectedItems.length) {
    value = `${firstItemOverflows ? firstItemOverflowText : value} ${
      messages?.andMoreText
        ? messages.andMoreText(selectedItems.length - visibleItemsCount)
        : defaultAndMoreItemsText(selectedItems.length - visibleItemsCount)
    }`;
  }

  return value;
};

const useSelectedItemsInputValue = (
  selectedItems: (Node<unknown> | null)[] | undefined,
  itemsTotalCount: number,
  compositeFieldRef: React.RefObject<HTMLDivElement>,
  inputRef: React.RefObject<HTMLInputElement>,
  shouldCompute: boolean,
  onItemsVisibleChange: (visible: boolean) => void,
  messages?: MessagesConfig,
  inputSize: 'small' | 'medium' | 'large' | undefined = 'medium',
) => {
  const [inputValue, setInputValue] = useState<string>('');
  const itemsRef = useRef<React.MutableRefObject<HTMLSpanElement>[]>(
    Array.from(
      {length: itemsTotalCount},
      () =>
        React.createRef<HTMLSpanElement>() as React.MutableRefObject<HTMLSpanElement>,
    ),
  );

  useLayoutEffect(() => {
    itemsRef.current = Array.from(
      {length: itemsTotalCount},
      () =>
        React.createRef<HTMLSpanElement>() as React.MutableRefObject<HTMLSpanElement>,
    );
  }, [itemsTotalCount]);

  useLayoutEffect(() => {
    if (
      !shouldCompute ||
      !selectedItems ||
      selectedItems.length === 0 ||
      !compositeFieldRef.current ||
      !inputRef.current ||
      !itemsRef.current
    ) {
      return;
    }

    const {visibleItemsCount, firstItemOverflows, firstItemOverflowText} =
      getVisibleItemsAndOverflow(
        selectedItems,
        inputRef,
        itemsRef,
        compositeFieldRef,
        textStylePropertiesBySize[inputSize],
      );

    const inputTextValue = getComputedInputValue(
      selectedItems,
      visibleItemsCount,
      firstItemOverflows,
      firstItemOverflowText,
      messages,
    );

    setInputValue(inputTextValue);

    onItemsVisibleChange(
      !firstItemOverflows && visibleItemsCount === selectedItems.length,
    );
  }, [
    selectedItems,
    compositeFieldRef,
    inputRef,
    shouldCompute,
    onItemsVisibleChange,
    messages,
    inputSize,
  ]);

  if (!shouldCompute || !selectedItems || selectedItems.length === 0) {
    return '';
  }

  return inputValue;
};

// TODO(craigcav): should probably use something l10n friendly here like `useFilter`.
function contains<T>(
  textValue: string,
  inputValue: string,
  _userItem: T,
): boolean {
  return textValue.toLowerCase().includes(inputValue.toLowerCase());
}

export interface SearchFieldOptions {
  /**
   * Whether or not the searchbox results should be rendered in a popover (defaults to true)
   */
  shouldUsePopover?: boolean;
}

interface SearchFieldContextValue {
  collection?: ListCollection<FauxTreeItemProps, FauxTreeGroupProps>;
  options?: SearchFieldOptions;
}

export const SearchFieldContext = createContext<SearchFieldContextValue>({});

function useSearchFieldState<T extends object>({
  children,
  data,
  item,
  group,
  ...props
}: SearchFieldProps<T>) {
  const {collection: inheritedCollection, options} =
    useContext(SearchFieldContext);

  // Track whether we're using inherited state or not so we can't violate the
  // rules of hooks by switching between the two.
  const isUsingInheritedCollection = useRef(Boolean(inheritedCollection));

  let collection: ListCollection<FauxTreeItemProps, FauxTreeGroupProps>;
  if (isUsingInheritedCollection.current) {
    if (inheritedCollection == null) {
      throw new Error(
        'Cannot switch from overriding the collection in SearchField to not overriding',
      );
    }
    collection = inheritedCollection;
  } else {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    collection = useListCollection({
      children,
      data,
      item,
      group,
      itemTransforms: FauxTreeItemTransforms,
      groupTransforms: FauxTreeGroupTransforms,
    });
  }

  const state = useMultiComboBoxState({
    ...props,
    inputValue: props.value,
    defaultInputValue: props.defaultValue,
    getItems: collection.getItems,
    selection: {
      ...props,
      ...normalizeSelectionProps(props, collection),
    },
    trigger: props,
    expandable: props,
    allowsCustomValue: true,
    filter: {
      // Only default to `contains` if no `defaultFilter` was provided,
      // otherwise use whatever the user provided (which may be a falsy value)
      defaultFilter:
        props.defaultFilter === undefined ? contains : props.defaultFilter,
    },
  });

  return {collection, state, options: options || {}};
}

export const SearchField = SearchFieldConfig.createGenericView(
  <T extends object>(originalProps: View.RenderProps<SearchFieldProps<T>>) => {
    const isMultiSelect = originalProps.selectionMode === 'multiple';
    const {valueAdapter, changeAdapter} =
      useSearchFieldFormAdapter(isMultiSelect);

    const changeProp = isMultiSelect
      ? 'onMultiSelectionChange'
      : 'onSelectionChange';

    const [fieldProps, props] = useFormFieldProps(originalProps, {
      valueProp: isMultiSelect ? 'selectedKeys' : 'selectedKey',
      changeProp,
      valueAdapter,
      changeAdapter,
    });

    const {label, disabled} = fieldProps;

    const {state, collection, options} = useSearchFieldState({
      ...props,
      label,
    });

    const {shouldUsePopover = true} = options;
    const {
      data,
      item,
      group,
      children,
      placeholder,
      onSubmit,
      required,
      readOnly,
      emptyState,
      size,
      messages,
      selectionAllEnabled = true,
      ...restProps
    } = props;

    const {
      searchIcon: searchFieldSubview,
      cancelAction: cancelActionSubview,
      textfield: textfieldSubview,
      ...subviews
    } = originalProps.subviews;

    validateSelectionProps(restProps);

    const {isTouch, isSmallScreen} = useMediaQuery();
    const isMobileAndTouch = isTouch && isSmallScreen;
    const ref = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const cancelActionRef = useRef<HTMLElement>(null);
    const compositeFieldRef = useRef<HTMLDivElement>(null);
    const asyncItemsRef = useRef<Map<Key, Node<unknown>>>(new Map());
    const [triggerTooltipEnabled, setTriggerTooltipEnabled] =
      useState<boolean>(false);
    const isInSailForm = !!useFormContext()?.control;

    const {
      inputProps,
      listBoxProps,
      labelProps,
      descriptionProps,
      errorMessageProps,
    } = useComboBox(
      {
        ...props,
        ...normalizeSelectionProps(props, collection),
        shouldFocusWrap: true,
        allowsCustomValue: true,
        isDisabled: disabled,
        isReadOnly: readOnly,
        label,
        placeholder,
        inputRef,
        buttonRef: cancelActionRef,
        listBoxRef: listRef,
        popoverRef: shouldUsePopover ? popoverRef : listRef,
      },
      state,
    );

    const itemsLength = useMemo(() => {
      return getCollectionItemsLength(collection);
    }, [collection]);

    const selectAllRef = useRef<HTMLInputElement>(null);

    const treeCollection = (state as TreeState).collection;

    const dataItems = useMemo(() => {
      const currentAsyncItems = getFullDataItems(treeCollection);

      const plainCurrentAsyncItems = [...currentAsyncItems.entries()];

      for (let i = 0; i < plainCurrentAsyncItems.length; i++) {
        const [key, item] = plainCurrentAsyncItems[i];

        if (!asyncItemsRef.current.has(key)) {
          asyncItemsRef.current.set(key, item);
        }
      }

      return asyncItemsRef.current;
    }, [treeCollection, asyncItemsRef]);

    let selectedItems = state.selectedItems;
    const selectedKeys = [...(state.selectedKeys ?? [])];

    const isAsyncWithSelectedkeys =
      !selectedItems?.length && selectedKeys.length && dataItems.size;

    // Note: it might be asynchronous if there are selected keys without any data
    if (isAsyncWithSelectedkeys) {
      selectedItems = selectedKeys.reduce((total: Node<unknown>[], key) => {
        const currentItem = dataItems.get(key) as Node<unknown> | undefined;

        if (currentItem) {
          total.push(currentItem);
        }

        return total;
      }, []);
    }

    const selectedItemsInputValue = useSelectedItemsInputValue(
      selectedItems,
      isAsyncWithSelectedkeys ? selectedItems?.length ?? 0 : itemsLength,
      fieldProps.ref || compositeFieldRef,
      inputRef,
      shouldUsePopover, // TODO: this prop is used to avoid doing
      // unncessary computations to get the input value when the SearchField
      // is used in the filterable SelectField. Although this prop is used by
      // the filterable SelectField component only as of today, we shouldn't tie
      // the prop to this behavior
      (allItemsVisible) => setTriggerTooltipEnabled(!allItemsVisible),
      messages,
      size,
    );

    const onKeyDown = (e: React.KeyboardEvent) => {
      const key = e.key;

      if (key === 'Enter' || key === 'Escape') {
        e.preventDefault();
      }

      if (key === 'Enter') {
        // We don't want to submit the input when enter is pressed and the input
        // is in composition (a composition is a temporary state of the input where
        // the user is typing a character that is not yet part of the input value,
        // for instance when using an IME).
        if (!e.nativeEvent.isComposing) {
          onSubmit?.(
            state.inputValue,
            new Set(state.collection.getKeys() as string[]),
            state.selectionManager.focusedKey,
          );
        }
      }

      if (key === 'Escape') {
        if (state.inputValue !== '') {
          e.stopPropagation();
        }
        state.setInputValue('');
      }
    };

    if (children) {
      throw new Error(
        'SearchField was provided children. This is not supported. You should provide items ' +
          'with the `data` prop',
      );
    }

    const fauxTreeContents = (
      <FocusScope restoreFocus>
        <FauxTree
          ref={listRef}
          label={label}
          data={data}
          item={item}
          group={group}
          emptyState={emptyState}
          inherits={subviews.listbox as View.Subview<FauxTreeProps<T>>}
          groupCounterEnabled={false}
          selectionAllEnabled={selectionAllEnabled}
          messages={{
            selectedText:
              messages?.selectedText ?? defaultSelectedTextForSearch,
          }}
        >
          {children}
        </FauxTree>
      </FocusScope>
    );

    const listboxContents = (
      <FocusScope restoreFocus>
        <ListBox
          ref={listRef}
          label={label}
          data={data}
          item={item}
          group={group}
          emptyState={emptyState}
          inherits={subviews.listbox as View.Subview<ListBoxProps<T>>}
          css={[
            css({
              height: 'fill',
              overflowY: 'auto',
              overflowX: 'hidden',
              overscrollBehavior: 'contain',
            }),
            isMobileAndTouch
              ? css({})
              : css({
                  // This should probably be viewport based, but this specific value
                  // is based on the existing select field implementation.
                  maxHeight: '480px',
                }),
          ]}
        />
      </FocusScope>
    );

    const list =
      props.selectionMode === 'multiple' ? (
        <FauxTreeContext.Provider
          value={{
            state,
            collection,
            options: {
              ...listBoxProps,
              autoFocus: true,
              selectAllRef,
            },
          }}
        >
          {shouldUsePopover ? (
            // The boolean above isn't actually for opening it, just whether to use it
            <Popover
              targetRef={ref}
              ref={popoverRef}
              onClose={() => state.close()}
              dismissible={
                props.menuTrigger === 'focus'
                  ? // Dismissal on outsidePress and outsideFocus is already handled
                    // by the combobox when trigger is 'focus', only listen for escapeKey.
                    DISMISS_ON_ESCAPE
                  : undefined
              }
              placement="bottom start"
              shift={false}
              size={{
                maxHeight: 'fill',
                matchWidth: true,
              }}
              css={{
                keyline: 'transparent',
                // TODO: due to border/box-shadow differences between the trigger and the popover,
                // we have to nudge the size and position of the popover to align with the trigger
                bleedX: 'space.1',
                stack: 'y',
              }}
              inherits={subviews.popover}
            >
              {fauxTreeContents}
            </Popover>
          ) : (
            fauxTreeContents
          )}
        </FauxTreeContext.Provider>
      ) : (
        <ListBoxContext.Provider
          value={{
            state,
            collection,
            options: {...listBoxProps, autoFocus: true},
          }}
        >
          {shouldUsePopover ? (
            // The boolean above isn't actually for opening it, just whether to use it
            <Popover
              targetRef={ref}
              ref={popoverRef}
              onClose={() => state.close()}
              dismissible={
                props.menuTrigger === 'focus'
                  ? // Dismissal on outsidePress and outsideFocus is already handled
                    // by the combobox when trigger is 'focus', only listen for escapeKey.
                    DISMISS_ON_ESCAPE
                  : undefined
              }
              placement="bottom start"
              shift={false}
              size={{
                maxHeight: 'fill',
                matchWidth: true,
              }}
              css={{
                keyline: 'transparent',
                // TODO: due to border/box-shadow differences between the trigger and the popover,
                // we have to nudge the size and position of the popover to align with the trigger
                bleedX: 'space.1',
                stack: 'y',
              }}
              inherits={subviews.popover}
            >
              {listboxContents}
            </Popover>
          ) : (
            listboxContents
          )}
        </ListBoxContext.Provider>
      );

    const multipleItemsVisibleInTrigger =
      props.selectionMode === 'multiple' && !state.isOpen && !state.isFocused;

    const isClearButtonVisible =
      !disabled &&
      !readOnly &&
      ((multipleItemsVisibleInTrigger && selectedItemsInputValue) ||
        (state.inputValue &&
          (props.selectionMode === 'single' || state.isFocused)));

    // Remove name from inputProps when inside a form, so that the form
    // controls the SearchField instead of the underlying TextField.
    const textFieldProps = isInSailForm
      ? {...inputProps, name: undefined}
      : inputProps;

    return (
      <CompositeFieldConfig.Customize
        subviews={{
          label: {uses: [assignProps(labelProps)]},
          description: {uses: [assignProps(descriptionProps)]},
          inputs: {
            css: state.selectionManager.focusedKey
              ? css({
                  ':focus-within': {
                    focusRing: 'none',
                  },
                })
              : css({
                  ':focus-within': {
                    focusRing: 'focus',
                  },
                }),
            uses: [
              assignProps({ref}),
              disabled &&
                css({
                  backgroundColor: 'form.disabled',
                  pointerEvents: 'none',
                }),
            ],
          },
          error: {uses: [assignProps(errorMessageProps)]},
        }}
      >
        <CompositeField
          {...fieldProps}
          ref={fieldProps.ref || compositeFieldRef}
          size={size}
          // use the old PopoverLayer until we refactor SearchField to be responsive
        >
          <Icon
            inherits={searchFieldSubview}
            icon={search}
            aria-hidden
            size="xsmall"
            key="search-icon"
          />
          <TextFieldConfig.Customize
            subviews={{
              input: css({
                '::-webkit-search-decoration': {
                  '-webkit-appearance': 'none',
                },
                '::-webkit-search-cancel-button': {
                  '-webkit-appearance': 'none',
                },
              }),
            }}
          >
            <TextField
              inherits={textfieldSubview}
              label={label}
              ref={inputRef}
              required={required}
              {...(textFieldProps as React.HTMLAttributes<HTMLInputElement>)}
              value={
                multipleItemsVisibleInTrigger
                  ? selectedItemsInputValue
                  : inputProps.value
              }
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                onKeyDown(e);
                inputProps.onKeyDown?.(e);
              }}
              autoComplete="off"
              readOnly={readOnly}
              disabled={disabled}
              autoFocus={props.autoFocus}
              // value is always "controlled" on inputProps, so clear any default value
              defaultValue={undefined}
              type="search"
              subviews={{input: subviews.input}}
              onBlur={(e) => {
                if (props.selectionMode === 'multiple') {
                  // Note: react-aria prevents the focus of the elements inside of the
                  // data listbox collection, keeping the focus in the search input. Since select All
                  // is not rendered by the collection and checkable groups are not inside of a ListBox,
                  // we have to emulate the behavior here.
                  // We stop propagating blur for components that are inside of the same overlay than
                  // FauxTree.
                  // TODO: Once FauxTree will be an actual react aria tree we can get rid of this logic
                  const potentialParentOverlay = e.relatedTarget
                    ? (e.relatedTarget as HTMLInputElement).closest(
                        '[data-overlay-container]',
                      )
                    : null;

                  const parentOverlay = listRef.current
                    ? (listRef.current as HTMLInputElement).closest(
                        '[data-overlay-container]',
                      )
                    : null;

                  const parentOverlayWasClicked =
                    potentialParentOverlay === parentOverlay &&
                    potentialParentOverlay !== null &&
                    parentOverlay !== null &&
                    e.relatedTarget !== null;

                  if (parentOverlayWasClicked) {
                    // eslint-disable-next-line @sail/ui-no-reflow
                    e.target.focus();
                    e.preventDefault();
                    e.stopPropagation();
                  } else {
                    inputProps.onBlur?.(e);
                    state.setInputValue('');
                  }
                } else {
                  inputProps.onBlur?.(e);
                }
              }}
            />
          </TextFieldConfig.Customize>
          <Action
            inherits={cancelActionSubview}
            ref={cancelActionRef}
            tabIndex={-1}
            aria-hidden
            onPress={() => {
              if (props.selectionMode === 'multiple' && !state.isOpen) {
                state.setSelectedKeys(new Set());
              } else {
                state.setInputValue('');
              }
            }}
            onPressStart={() => {
              // this is in PressStart for mobile so that touching the clear button doesn't remove focus from
              // the input and close the keyboard
              // eslint-disable-next-line @sail/ui-no-reflow
              requestAnimationFrame(() => inputRef.current?.focus());
            }}
            disabled={disabled || readOnly}
            css={[
              isClearButtonVisible
                ? css({visibility: 'visible'})
                : css({visibility: 'hidden'}),
              css({
                // TODO: shouldn't need stack or alignment here
                // but the visual regression tests show that the icon is not
                // vertically centered without it (despite looking OK in a browser)
                stack: 'z',
                display: 'inline',
                alignY: 'center',
                alignSelfY: 'center',
              }),
            ]}
          >
            <Icon icon={cancelCircleFilled} aria-hidden size="xsmall" />
          </Action>
        </CompositeField>
        {multipleItemsVisibleInTrigger &&
        triggerTooltipEnabled &&
        selectedItems &&
        (selectedItems ?? []).length > 0 ? (
          <Tooltip
            trigger={inputRef}
            placement="top"
            delay={{show: 0, hide: 0}}
          >
            <view.div
              css={{
                display: '-webkit-box',
                lineClamp: 10,
                WebkitLineClamp: 10,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {(selectedItems ?? []).map((item) => item?.textValue).join(', ')}
            </view.div>
          </Tooltip>
        ) : null}

        {state.isOpen ? list : null}
      </CompositeFieldConfig.Customize>
    );
  },
);
